import { RefObject, useEffect, useRef } from 'react';

interface IClickAwayProps {
  ref: RefObject<HTMLElement | null>;
  open: boolean;
  onClickAway?: (event: MouseEvent) => void;
  onClickInside?: (event: MouseEvent) => void;
  onEscape?: () => void;
  onTab?: (props: { indexOfActiveElement: number; focusableElementsLength: number }) => void;
  onShiftTab?: (props: { indexOfActiveElement: number; focusableElementsLength: number }) => void;
  /** NOTE: onTabIn is a work in progress, it's a little buggy */
  onTabIn?: (props: { shiftKeyDown: boolean }) => void;
  /** NOTE: onTabAway is a work in progress, it's a little buggy */
  onTabAway?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: MouseEvent) => void;
  onPointerDownInside?: (event: MouseEvent) => void;
  onPointerUpOutside?: (event: MouseEvent) => void;
  onPointerUpInside?: (event: MouseEvent) => void;
  resetFocusedOnEscape?: boolean;
  ignoreClasses?: string[];
}
/**
 *
 * This hook listens for clicks outside of a given ref and calls the appropriate callback.
 *
 * ***It does not support touch screens.***
 */
const useClickAwayListener = ({
  ref,
  onClickAway,
  onClickInside,
  onPointerDownOutside,
  onPointerUpOutside,
  onPointerDownInside,
  onPointerUpInside,
  onEscape,
  onTab,
  onTabIn,
  onShiftTab,
  onTabAway,
  open,
  resetFocusedOnEscape = true,
  ignoreClasses = [],
}: IClickAwayProps): void => {
  const isFocusedIn = useRef(false);

  useEffect(() => {
    if (!open) {
      isFocusedIn.current = false;
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!open) return;

      if (!(event.target instanceof Node)) {
        return;
      }

      if (
        ignoreClasses.length > 0 &&
        event.target instanceof Element &&
        ignoreClasses.some((className) => (event.target as Element).closest(className))
      ) {
        return;
      }
      // debugger;
      if (!ref?.current?.contains?.(event.target)) {
        isFocusedIn.current = false;
        onClickAway?.(event);
      } else {
        isFocusedIn.current = true;
        onClickInside?.(event);
      }
    };

    const handlePointerDown = (event: MouseEvent) => {
      if (!open) return;
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      if (!ref?.current?.contains?.(event.target)) {
        onPointerDownOutside?.(event);
        isFocusedIn.current = false;
      } else {
        onPointerDownInside?.(event);
        isFocusedIn.current = true;
      }
    };
    const handlePointerUp = (event: MouseEvent) => {
      if (!open) return;
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      if (!ref?.current?.contains?.(event.target)) {
        onPointerUpOutside?.(event);
        isFocusedIn.current = false;
      } else {
        onPointerUpInside?.(event);
        isFocusedIn.current = true;
      }
    };

    const handleKeypress = (event: KeyboardEvent) => {
      // on escape
      if (open && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onEscape?.();
        if (resetFocusedOnEscape) {
          isFocusedIn.current = false;
        }
      }

      // on tab
      if (open && event.key === 'Tab' && !event.shiftKey) {
        const focusableElements = ref.current?.querySelectorAll(
          'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable]'
        );
        if (onTab) {
          const indexOfActiveElement = Array.from(focusableElements || []).findIndex(
            (el) => el === document.activeElement
          );
          onTab({ indexOfActiveElement, focusableElementsLength: focusableElements?.length || 0 });
        }

        if (onTabAway || onTabIn) {
          const lastFocusableElement = focusableElements?.[focusableElements.length - 1] as HTMLElement;
          const previouslyOnLastItem = lastFocusableElement === document.activeElement;
          if (previouslyOnLastItem) {
            onTabAway?.(event);
            isFocusedIn.current = false;
          }

          // if ref contains active element and !isFocusedIn.current
          // then we know that the user has tabbed into the ref
          const firstFocusableElement = focusableElements?.[0] as HTMLElement;
          if (
            (ref.current === document.activeElement || ref.current === firstFocusableElement) &&
            !isFocusedIn.current
          ) {
            onTabIn?.({ shiftKeyDown: false });
            isFocusedIn.current = true;
          }
        }
      }

      // on shift tab
      if (open && event.key === 'Tab' && event.shiftKey) {
        const focusableElements = ref.current?.querySelectorAll(
          'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable]'
        );
        // get index of active element
        if (onShiftTab) {
          const indexOfActiveElement = Array.from(focusableElements || []).findIndex(
            (el) => el === document.activeElement
          );
          onShiftTab({ indexOfActiveElement, focusableElementsLength: focusableElements?.length || 0 });
        }
        if (onTabAway || onTabIn) {
          const firstFocusableElement = focusableElements?.[0] as HTMLElement;
          const previouslyOnFirstItem = firstFocusableElement === document.activeElement;
          if (previouslyOnFirstItem) {
            onTabAway?.(event);
            isFocusedIn.current = false;
          }

          // if ref contains active element and !isFocusedIn.current
          // then we know that the user has tabbed into the ref
          const lastFocusableElement = focusableElements?.[focusableElements.length - 1] as HTMLElement;
          if (
            (ref.current === document.activeElement || ref.current === lastFocusableElement) &&
            !isFocusedIn.current
          ) {
            onTabIn?.({ shiftKeyDown: true });
            isFocusedIn.current = true;
          }
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    if (onPointerDownOutside || onPointerDownInside) {
      document.addEventListener('pointerdown', handlePointerDown);
    }
    if (onPointerUpOutside || onPointerUpInside) {
      document.addEventListener('pointerup', handlePointerUp);
    }

    document.addEventListener('keydown', handleKeypress);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('keydown', handleKeypress);
    };
  }, [
    ref,
    onClickAway,
    onClickInside,
    onEscape,
    open,
    onTab,
    onTabIn,
    onShiftTab,
    onTabAway,
    onPointerDownOutside,
    onPointerDownInside,
    onPointerUpOutside,
    onPointerUpInside,
    ignoreClasses,
    resetFocusedOnEscape,
  ]);
};

export default useClickAwayListener;
